import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import axios from "axios";
import Spinner from "../../Chat/components/Spinner";
import { FaRegComment } from "react-icons/fa";
import { FiX, FiRefreshCw, FiPlay, FiClock, FiGlobe } from "react-icons/fi";
import { MdOutlineAdd } from "react-icons/md";

const WordList = ({ wordList, initialUser, onWordAdded, onWordDeleted }) => {
  const [words, setWords] = useState(wordList?.words || []);
  const [newWord, setNewWord] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(initialUser);
  const [expandedSentences, setExpandedSentences] = useState({});
  const [error, setError] = useState(null);
  const [regeneratingWordIds, setRegeneratingWordIds] = useState({});
  const [inputMode, setInputMode] = useState("english"); // "english" or "target"

  const isEnglishList = wordList?.languageName === "English";

  useEffect(() => {
    setUser(initialUser);
  }, [initialUser]);

  // If no wordList exists, show create message
  if (!wordList) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <p className="text-gray-500 text-lg mb-4">
          No word list found. Please create a word list first.
        </p>
        <p className="text-gray-400 text-sm">
          You'll need to create a word list before you can add words to practice
          with.
        </p>
      </div>
    );
  }

  const addWord = async (e) => {
    e.preventDefault();
    if (!newWord.trim() || isLoading || user.wordsRemaining <= 0) return;

    setIsLoading(true);
    setError(null);

    try {
      const payload = { word: newWord };
      if (inputMode === "english" && !isEnglishList) {
        payload.from_english = true;
      }

      const response = await axios.post(
        `/api/v1/word_lists/${wordList.id}/words`,
        payload,
      );
      const addedWordData = response.data;
      const updatedUser = response.data.user;

      setNewWord("");
      setUser(updatedUser);

      setWords((prevWords) => {
        const addedWord = {
          ...addedWordData.word,
          allowRegenerate: true,
        };
        const prevWordsWithoutRegenerate = prevWords.map((word) => ({
          allowRegenerate: false,
          ...word,
        }));
        return [addedWord, ...prevWordsWithoutRegenerate];
      });

      if (onWordAdded) {
        onWordAdded(
          { ...wordList, words: [addedWordData.word, ...words] },
          updatedUser,
        );
      }
    } catch (error) {
      console.error("Error adding word:", error);

      if (error.response && error.response.data) {
        const errData = error.response.data;
        if (
          errData.error === "Language validation failed" ||
          errData.error === "Translation failed"
        ) {
          setError(
            <div>
              <p className="font-semibold mb-1">{errData.error}:</p>
              <p>{errData.message}</p>
            </div>,
          );
        } else {
          setError("Failed to add word. Please try again.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateSentence = async (wordId) => {
    setRegeneratingWordIds((prev) => ({ ...prev, [wordId]: true }));

    try {
      const response = await axios.post(
        `/api/v1/word_lists/${wordList.id}/words/${wordId}/regenerate_sentence`,
      );

      const updatedWordData = response.data;

      setWords((prevWords) =>
        prevWords.map((word) =>
          word.id === wordId
            ? { ...updatedWordData.word, allowRegenerate: true }
            : word,
        ),
      );
    } catch (error) {
      console.error("Error regenerating sentence:", error);
    } finally {
      setRegeneratingWordIds((prev) => ({ ...prev, [wordId]: false }));
    }
  };

  const deleteWord = async (wordId) => {
    try {
      await axios.delete(`/api/v1/word_lists/${wordList.id}/words/${wordId}`);

      setWords(words.filter((word) => word.id !== wordId));

      if (onWordDeleted) {
        onWordDeleted(wordId);
      }
    } catch (error) {
      console.error("Error deleting word:", error);
    }
  };

  const playAudio = (audioUrl, rate = 1) => {
    const audio = new Audio(audioUrl);
    audio.playbackRate = rate;
    audio.play();
  };

  const toggleSentence = (wordId) => {
    setExpandedSentences((prev) => ({
      ...prev,
      [wordId]: !prev[wordId],
    }));
  };

  const truncateSentence = (sentence, wordId) => {
    if (!sentence) return "";
    if (expandedSentences[wordId]) return sentence;

    const maxLength = 60;
    if (sentence.length <= maxLength) return sentence;
    return sentence.substring(0, maxLength) + "...";
  };

  const inputPlaceholder =
    inputMode === "english" && !isEnglishList
      ? `Type an English word (e.g. "cat")`
      : `Enter a ${wordList.languageName} word`;

  return (
    <div className="space-y-6">
      <div className="bg-blue-100 p-4 rounded-lg">
        <p className="text-lg font-semibold">
          Language:{" "}
          <span className="text-blue-600">{wordList.languageName}</span>
        </p>
      </div>
      {user.wordsRemaining > 0 ? (
        <div className="space-y-2">
          {/* Input Mode Toggle */}
          {!isEnglishList && (
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
              <button
                type="button"
                onClick={() => setInputMode("english")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  inputMode === "english"
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Type in English
              </button>
              <button
                type="button"
                onClick={() => setInputMode("target")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  inputMode === "target"
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Type in {wordList.languageName}
              </button>
            </div>
          )}

          <form onSubmit={addWord} className="flex space-x-2">
            <div className="flex-grow relative">
              <input
                type="text"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                placeholder={inputPlaceholder}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
              {inputMode === "english" && !isEnglishList && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  EN → {wordList.languageName}
                </span>
              )}
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? <Spinner /> : "Add Word"}
            </button>
          </form>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
        </div>
      ) : (
        <p className="text-red-500">
          You have reached the maximum number of words for this week. Please
          upgrade to add more or wait until next week.
        </p>
      )}

      {user.freemium && (
        <p className="text-sm text-gray-600">
          Words remaining: {user.wordsRemaining}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {words.map((word) => (
          <div
            key={word.id}
            className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition duration-200 flex flex-col h-full"
          >
            <div className="flex">
              {/* Image thumbnail from BaseWord */}
              {word.imageUrl && (
                <div className="w-16 h-16 flex-shrink-0 bg-gray-100 m-3 rounded-lg overflow-hidden">
                  <img
                    src={word.imageUrl}
                    alt={word.english_text || word.word_text}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}

              <div className="p-4 flex-grow min-w-0">
                <div className="flex justify-between items-start mb-1">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-lg text-gray-800 truncate">
                      {word.word_text}
                    </h3>
                    {word.english_text &&
                      word.english_text !== word.word_text && (
                        <p className="text-sm text-indigo-500">
                          {word.english_text}
                        </p>
                      )}
                  </div>
                  <button
                    onClick={() => deleteWord(word.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 flex-shrink-0 ml-2"
                    aria-label="Delete word"
                  >
                    <FiX className="h-5 w-5" />
                  </button>
                </div>

                {word.sentence && (
                  <div className="mt-1 text-gray-600 text-sm">
                    <p className="italic">
                      {truncateSentence(word.sentence, word.id)}
                    </p>
                    {word.sentence.length > 60 && (
                      <button
                        onClick={() => toggleSentence(word.id)}
                        className="text-blue-500 hover:text-blue-700 text-xs mt-1"
                      >
                        {expandedSentences[word.id] ? "Show less" : "Read more"}
                      </button>
                    )}
                    {word.allowRegenerate && (
                      <div className="mt-1">
                        <button
                          onClick={() => regenerateSentence(word.id)}
                          className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
                          disabled={regeneratingWordIds[word.id]}
                        >
                          {regeneratingWordIds[word.id] ? (
                            <>
                              <span className="inline-block h-3 w-3 mr-1">
                                <Spinner />
                              </span>
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <FiRefreshCw className="h-3 w-3 mr-1" />
                              Regenerate sentence
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 p-3 border-t border-gray-100 mt-auto">
              <div className="flex justify-between">
                {word.audioWordUrl && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => playAudio(word.audioWordUrl)}
                      className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <FiPlay className="h-5 w-5 mr-1" />
                      Play Word
                    </button>
                    <button
                      onClick={() => playAudio(word.audioWordUrl, 0.75)}
                      className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <FiClock className="h-5 w-5 mr-1" />
                      Slow
                    </button>
                  </div>
                )}

                {word.audioSentenceUrl && (
                  <button
                    onClick={() => playAudio(word.audioSentenceUrl)}
                    className="flex items-center text-gray-600 hover:text-gray-800 text-sm"
                  >
                    <FiPlay className="h-5 w-5 mr-1" />
                    Sentence
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {words.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <MdOutlineAdd className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">
            No words added yet. Add your first word above!
          </p>
          {!isEnglishList && (
            <p className="text-gray-400 text-sm mt-1">
              Tip: You can type English words and we'll translate them for you.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

WordList.propTypes = {
  wordList: PropTypes.shape({
    id: PropTypes.number.isRequired,
    languageName: PropTypes.string.isRequired,
    words: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        word_text: PropTypes.string.isRequired,
      }),
    ),
  }),
  initialUser: PropTypes.shape({
    wordsRemaining: PropTypes.number.isRequired,
    freemium: PropTypes.bool.isRequired,
  }).isRequired,
  onWordAdded: PropTypes.func,
  onWordDeleted: PropTypes.func,
};

export default WordList;
